import {PoliciesApi} from 'Agent/Policies/api';
import {
  CorrespondenceDetailsApi,
  CorrespondencesApi,
  DetailsApi,
  DocumentApi,
  ImagesApi,
  PendingUnderwritingRequirementsApi,
  PolicyDto,
} from 'Agent/Policies/api/api';
import {CorrespondenceLetterSummary} from 'Agent/Policies/api/apiTypes';
import {PolicyFilter} from 'Agent/Policies/ducks/policy';
import {PolicyApplicationsFilter} from 'Agent/Policies/ducks/policyApplications';
import {
  PolicyCorrespondenceDetailFilter,
  PolicyCorrespondenceSummaryFilter,
} from 'Agent/Policies/ducks/policyCorrespondence';
import {AxiosResponse} from 'axios';
import {AsyncResult} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import axiosInstance from 'Shared/utilities/axiosInstance';
import {getAxiosConfig, handleResponse, withCancellation} from 'Shared/utilities/fetchHelpers';
import {PendingUnderwritingRequirementDto, PolicyApplicationDto, PolicyApplicationsApi} from './api';
import {Sort, toSortQuerystringParams} from 'Shared/components/PagedView/Table/Sort';

const detailsApi = new DetailsApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const policiesApi = new PoliciesApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const correspondencesApi = new CorrespondencesApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const correspondenceDetailsApi = new CorrespondenceDetailsApi(
  undefined,
  process.env.REACT_APP_BASE_GATEWAY_URL,
  axiosInstance
);
const imagesApi = new ImagesApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const policyDocumentApi = new DocumentApi(undefined, process.env.REACT_APP_BASE_GATEWAY_URL, axiosInstance);
const pendingUnderwritingRequirementApi = new PendingUnderwritingRequirementsApi(
  undefined,
  process.env.REACT_APP_BASE_GATEWAY_URL,
  axiosInstance
);
const policyApplicationsApi = new PolicyApplicationsApi(
  undefined,
  process.env.REACT_APP_BASE_GATEWAY_URL,
  axiosInstance
);

export default {
  getImages: (encryptedIdentifier: string, policyId: string) =>
    imagesApi
      .getPolicyImageById(policyId, '1', {params: {encryptedIdentifier}, ...getAxiosConfig()})
      .then(handleResponse),
  getPolicy: (encryptedIdentifier: string, policyId: string, policyNumber: string) =>
    detailsApi
      .getPolicyById(policyId, policyNumber, '1', {params: {encryptedIdentifier}, ...getAxiosConfig(undefined, false)})
      .then(handleResponse) as Promise<AsyncResult<PolicyDto>>,
  getCorrespondenceSummaries: (
    agentNumbers: string[],
    isNewYork: boolean,
    encryptedIdentifier: string,
    filter?: PolicyCorrespondenceSummaryFilter,
    sort?: Sort
  ) => {
    return withCancellation(cancelToken =>
      correspondencesApi.searchCorrespondenceLetters('1', {
        params: {agentNumbers, isNewYork, encryptedIdentifier, ...filter, ...(sort && toSortQuerystringParams(sort))},
        ...getAxiosConfig(cancelToken, false),
      })
    ).then(handleResponse) as Promise<AxiosResponse<Page<CorrespondenceLetterSummary>>>;
  },
  getCorrespondenceDetails: async (filter: PolicyCorrespondenceDetailFilter) => {
    const response = await correspondenceDetailsApi.getCorrespondenceDetails('1', {
      params: filter,
      ...getAxiosConfig(undefined, false),
    });
    return handleResponse(response);
  },
  searchPolicies: (
    agentNumbers: string[],
    isNewYork: boolean,
    encryptedIdentifier: string,
    filter?: PolicyFilter,
    sort?: Sort
  ) => {
    return (withCancellation(cancelToken =>
      policiesApi.searchPolicies('1', {
        params: {agentNumbers, isNewYork, encryptedIdentifier, ...filter, ...(sort && toSortQuerystringParams(sort))},
        ...getAxiosConfig(cancelToken, false),
      })
    ).then(handleResponse) as unknown) as Promise<AsyncResult<Page<PolicyDto>>>;
  },
  uploadPolicyDocument: async (
    encryptedIdentifier: string,
    file: File,
    policyUploadDocumentId: string,
    policyId: string,
    policyNumber: string
  ) => {
    const response = await policyDocumentApi.uploadPolicyDocument(
      policyId,
      '1',
      policyNumber,
      Number(policyUploadDocumentId),
      file,
      {
        params: {encryptedIdentifier},
        ...getAxiosConfig(undefined),
      }
    );

    return handleResponse(response);
  },
  getPendingUnderwritingRequirements: async (
    encryptedIdentifier: string,
    agentNumbers: string[],
    isNewYork: boolean
  ) => {
    const response = await pendingUnderwritingRequirementApi.getPendingUnderwritingRequirements(
      '1',
      agentNumbers,
      isNewYork,
      false,
      {params: {encryptedIdentifier}, ...getAxiosConfig(undefined)}
    );

    return handleResponse(response as AxiosResponse<PendingUnderwritingRequirementDto[]>);
  },
  getPolicyApplications: (encryptedIdentifier: string, filter: PolicyApplicationsFilter, sort?: Sort) => {
    return (withCancellation(cancelToken =>
      policyApplicationsApi.searchPolicyApplications('1', {
        params: {encryptedIdentifier, ...filter, ...(sort && toSortQuerystringParams(sort))},
        ...getAxiosConfig(cancelToken),
      })
    ).then(handleResponse) as unknown) as Promise<AsyncResult<Page<PolicyApplicationDto>>>;
  },
};
